import { useState, useId, useRef, useEffect } from "react";
import styles from "./styles.module.scss";
import { useTranslation } from "react-i18next";
import { useLanguage } from "context";

// Utility functions with safe date handling
const toDate = (dateInput) => new Date(dateInput);
const startOfMonthUTC = (dateInput) => {
  const d = toDate(dateInput);
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1));
};
const generateUTCDays = (monthInput) => {
  const d = toDate(monthInput);
  const year = d.getUTCFullYear();
  const month = d.getUTCMonth();
  const daysInMonth = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  return Array.from(
    { length: daysInMonth },
    (_, i) => new Date(Date.UTC(year, month, i + 1))
  );
};
const isSameUTCDay = (date1, date2) => {
  const d1 = toDate(date1);
  const d2 = toDate(date2);
  return (
    d1.getUTCFullYear() === d2.getUTCFullYear() &&
    d1.getUTCMonth() === d2.getUTCMonth() &&
    d1.getUTCDate() === d2.getUTCDate()
  );
};

const Datepicker = ({
  selectedDate,
  onDateChange,
  minDate = new Date(),
  error,
  labelAbove = false,
  label,
  required,
  workingHours = {},
  styled = true,
  boldLabel = false,
}) => {
  // Convert props to Date objects
  const { t, i18n } = useTranslation("global");
  const { language } = useLanguage(); // Get current language from your context
  const minDateObj = toDate(minDate);
  const selectedDateObj = selectedDate ? toDate(selectedDate) : null;

  const [isOpen, setIsOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(
    startOfMonthUTC(selectedDateObj || new Date())
  );
  const id = useId();
  const ref = useRef();

  const days = generateUTCDays(currentMonth);
  const firstDayOfMonth = toDate(currentMonth).getUTCDay();
  const emptyDays = Array(firstDayOfMonth).fill(null);

  // Get localized day names based on current language
  const getLocalizedDayNames = () => {
    // Use the language from your context or fallback to i18n language
    const currentLang = language || i18n.language || 'en';
    
    // Create a date for Monday to get all day names
    const baseDate = new Date(Date.UTC(2023, 0, 1)); // Sunday, January 1, 2023
    const dayNames = [];
    
    for (let i = 0; i < 7; i++) {
      const date = new Date(baseDate);
      date.setUTCDate(baseDate.getUTCDate() + i);
      const dayName = date.toLocaleDateString(currentLang, { 
        weekday: 'short',
        timeZone: 'UTC'
      });
      dayNames.push(dayName);
    }
    
    return dayNames;
  };

  // Get localized month name
  const getLocalizedMonthName = (date) => {
    const currentLang = language || i18n.language || 'en';
    return date.toLocaleDateString(currentLang, {
      month: "long",
      year: "numeric",
      timeZone: "UTC",
    });
  };

  const handleDateSelect = (date) => {
    if (isDateDisabled(date)) return;
    onDateChange(date);
    setIsOpen(false);
  };

  const isDateDisabled = (date) => {
    const dateUTC = toDate(date);
    const minUTC = toDate(minDateObj);

    // Check if date is before minimum date
    if (dateUTC < minUTC) return true;

    // Only check working hours if they're provided
    if (Object.keys(workingHours).length === 0) return false;

    // Check if there are working hours for this day
    return !workingHours[dateUTC.getUTCDay()]?.length;
  };

  const navigateMonth = (direction) => {
    const newDate = new Date(currentMonth);
    newDate.setUTCMonth(newDate.getUTCMonth() + direction);
    setCurrentMonth(startOfMonthUTC(newDate));
  };

  const formatUTCDate = (dateInput) => {
    if (!dateInput) return t("searchBar.datePlaceholder");
    const d = toDate(dateInput);
    
    // Use localized formatting based on current language
    const currentLang = language || i18n.language || 'en';
    
    // Different date formats based on language preference
    if (currentLang === 'en' || currentLang.startsWith('en-')) {
      // US format: MM/DD/YY
      return `${(d.getUTCMonth() + 1)
        .toString()
        .padStart(2, "0")}/${d.getUTCDate().toString().padStart(2, "0")}/${d.getUTCFullYear().toString().slice(-2)}`;
    } else {
      // European format: DD/MM/YY
      return `${d.getUTCDate().toString().padStart(2, "0")}/${(
        d.getUTCMonth() + 1
      )
        .toString()
        .padStart(2, "0")}/${d.getUTCFullYear().toString().slice(-2)}`;
    }
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Update calendar when language changes
  useEffect(() => {
    // This will force re-render of the calendar with new language
    setCurrentMonth(prev => new Date(prev)); // Trigger re-render
  }, [language, i18n.language]);

  // Prevent form submission when interacting with calendar
  const handleCalendarInteraction = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const localizedDayNames = getLocalizedDayNames();

  return (
    <div
      className={`${styles.customDatePicker} ${
        labelAbove ? styles.labelAbove : ""
      }`}
      ref={ref}
    >
      {label && (
        <label
          htmlFor={id}
          className={styles.label}
          onClick={() => setIsOpen(!isOpen)}
          style={{
            fontWeight: boldLabel ? "600" : "normal",
            fontSize: boldLabel ? "16px" : "14px",
          }}
        >
          {label}
          {required && <span className={styles.required}>*</span>}
        </label>
      )}

      <div className={styles.inputContainer}>
        <div
          id={id}
          className={
            styled ? styles.datePickerInput : styles.unstyledDatePickerInput
          }
          onClick={() => setIsOpen(!isOpen)}
          role="button"
        >
          {formatUTCDate(selectedDateObj)}
        </div>
      </div>

      {isOpen && (
        <>
          <div
            className={styles.dropdownBackdrop}
            onClick={() => setIsOpen(false)}
          />
          <div 
            className={styles.calendar}
            onClick={handleCalendarInteraction}
            onMouseDown={handleCalendarInteraction}
          >
            <div className={styles.header}>
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  navigateMonth(-1);
                }}
                onMouseDown={(e) => e.preventDefault()}
                aria-label={t("datepicker.previousMonth") || "Previous month"}
              >
                ←
              </button>
              <h3>
                {getLocalizedMonthName(toDate(currentMonth))}
              </h3>
              <button 
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  navigateMonth(1);
                }}
                onMouseDown={(e) => e.preventDefault()}
                aria-label={t("datepicker.nextMonth") || "Next month"}
              >
                →
              </button>
            </div>

            <div className={styles.grid}>
              {localizedDayNames.map((day) => (
                <div key={day} className={styles.dayNames}>
                  {day}
                </div>
              ))}
              {emptyDays.map((_, i) => (
                <div key={`empty-${i}`} className={styles.emptyDay} />
              ))}

              {days.map((date) => {
                const utcDate = toDate(date);
                const disabled = isDateDisabled(utcDate);
                const isSelected =
                  selectedDateObj && isSameUTCDay(utcDate, selectedDateObj);

                return (
                  <button
                    key={utcDate.toISOString()}
                    type="button"
                    className={`${styles.day} ${
                      isSelected ? styles.selected : ""
                    } ${disabled ? styles.disabled : ""}`}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleDateSelect(utcDate);
                    }}
                    onMouseDown={(e) => e.preventDefault()}
                    disabled={disabled}
                    aria-label={`${t("datepicker.selectDate") || "Select"} ${utcDate.toUTCString().slice(0, 16)}`}
                  >
                    {utcDate.getUTCDate()}
                  </button>
                );
              })}
            </div>
          </div>
        </>
      )}
      {error && <div className={styles.error}>{error}</div>}
    </div>
  );
};

export default Datepicker;